/**
 * One exchange, rendered the same way wherever it is read (RFC 0289 §6).
 *
 * EXTRACTED, not written. This was inline in `CommentsSection`, which was fine
 * while the side column was the only surface that could show a thread. RFC 0289
 * S5 gives the document editor one too, and a second copy of "root, replies,
 * reply box, resolve" is how the two surfaces start disagreeing about what a
 * resolved thread looks like or whether a failed send keeps the draft.
 *
 * SURFACE-AGNOSTIC BY CONSTRUCTION. It takes a thread and two callbacks and
 * holds no opinion about where it is mounted, which repo it writes through, or
 * whether replying should wake a session. The side column answers through
 * `answerComment`; so does the editor. That policy lives at the mount, not here.
 */

import {
  type Candidate,
  type CommentAuthor,
  type CommentEntry,
  type CommentTarget,
  type CommentThread,
  type Mention,
  projectAuthor,
} from '@mod/core/model'
import { useState } from 'react'
import { CommentRow } from '../../sdk/ui/comments'
import type {
  CommentReader,
  ThreadWaiting,
} from '../../services/sessions/commentReader'
import { sessionFace } from '../../services/sessions/sessionFace'
import { MentionComposer } from './MentionComposer'
import { EMPTY_DRAFT, type MentionDraft, verifyMentions } from './mentionDraft'

/** A composer with nothing to offer. Shared so an absent `candidates` prop does
 *  not mint a new function every render and reopen the dropdown. */
const noCandidates = (): Candidate[] => []

/** Relative time, coarse on purpose: a comment column wants "2h", not a date. */
export function ago(then: number, now: number): string {
  const secs = Math.max(0, Math.round((now - then) / 1000))
  if (secs < 60) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.round(hours / 24)}d`
}

/**
 * What an anchor is CALLED, for the heading of its block.
 *
 * Empty when the anchor says nothing more than "this document", which is the
 * common case and wants no heading at all rather than one reading "document".
 */
export function anchorLabel(anchor: CommentTarget): string {
  if (anchor.path) {
    return anchor.hunk ? `${anchor.path}:${anchor.hunk}` : anchor.path
  }
  if (anchor.anchorText) return anchor.anchorText
  if (anchor.messageId) return 'on a message'
  return ''
}

/** What a failed send says, when the surface gets no more than an Error. */
export function sendFailureMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? '')
  const line = raw.trim().split('\n', 1)[0] ?? ''
  return line ? `Not sent: ${line}` : 'Not sent.'
}

/** One exchange: a root, its answers, and the box to add another. */
export function CommentThreadView({
  thread,
  now,
  onReply,
  onResolve,
  candidates,
  authorOf = unknownProject,
  readerFor,
  waitingFor,
}: {
  thread: CommentThread
  now: number
  onReply: (
    parentId: string,
    body: string,
    mentions: Mention[],
  ) => Promise<void>
  onResolve: (commentId: string, resolved: boolean) => Promise<void>
  /** What can be named from here (RFC 0292). Absent = a plain reply box, which
   *  is what a surface that cannot resolve candidates should show rather than a
   *  picker that never offers anything. */
  candidates?: (query: string) => Candidate[]
  /** Who wrote a comment (RFC 0324 3a). The mount passes `projectAuthor`
   *  over its project, so a name is looked up in one place. */
  authorOf?: (comment: CommentEntry) => CommentAuthor
  /** Who a draft would reach (RFC 0324 3c). Absent = no line, for a mount
   *  that cannot see the space. */
  readerFor?: (mentions: Mention[], replyTo?: string) => CommentReader
  /** What the thread is waiting for (RFC 0324 3d). Absent = no line. */
  waitingFor?: (thread: CommentThread) => ThreadWaiting | null
}) {
  const [draft, setDraft] = useState<MentionDraft>(EMPTY_DRAFT)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const resolved = thread.root.resolved

  const send = async () => {
    const body = draft.text.trim()
    if (!body || sending) return
    setSending(true)
    setError(null)
    try {
      // Verified against the text as it stands, not as it stood when each chip
      // was chosen: a person who edited above one moved it, and a mention that
      // no longer covers the name it was written on names nothing.
      await onReply(
        thread.root.id,
        body,
        verifyMentions(draft.text, draft.mentions),
      )
      // Cleared only on SUCCESS: a failed send that wiped the box would lose
      // text somebody wrote, with nothing on screen to say where it went.
      setDraft(EMPTY_DRAFT)
    } catch (err) {
      // KEEPING THE DRAFT IS NOT ENOUGH ON ITS OWN. Before this, a rejected
      // send left the text sitting in the box looking exactly like a draft
      // nobody had pressed Enter on, so the failure read as "I forgot to send
      // it" and the person pressed Enter again into the same rejection.
      setError(sendFailureMessage(err))
    } finally {
      setSending(false)
    }
  }

  return (
    // Its own rounded card, no border (RFC 0324 design).
    <li
      className={`rounded-xl bg-muted/40 px-3 py-2.5 ${resolved ? 'opacity-55' : ''}`}
    >
      <CommentBody
        comment={thread.root}
        now={now}
        author={authorOf(thread.root)}
      />
      {thread.replies.length > 0 && (
        // Flat and unindented (RFC 0324 design): a reply lines up with the
        // first comment's avatar. Replies are flat by construction anyway,
        // `parentId` always points at the root.
        <ul className="mt-3 flex flex-col gap-3">
          {thread.replies.map((r) => (
            <li key={r.id}>
              <CommentBody comment={r} now={now} author={authorOf(r)} />
            </li>
          ))}
        </ul>
      )}
      {waitingFor ? <WaitingLine waiting={waitingFor(thread)} /> : null}
      {/* The reply box, per the design: a rounded box with the text on top
          and a toolbar under it, then who will read it. Enter sends,
          shift-Enter breaks the line; while the `@` picker is open Enter
          belongs to the picker, which MentionComposer handles. */}
      {!resolved && (
        <div className="mt-3 rounded-xl border border-border bg-background/40 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
          <div className="flex px-3 pt-2">
            <MentionComposer
              draft={draft}
              onDraftChange={setDraft}
              onSubmit={() => void send()}
              candidates={candidates ?? noCandidates}
              placeholder="Reply…"
              disabled={sending}
              className="w-full resize-none bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground/60"
            />
          </div>
          <div className="mt-1.5 flex items-center justify-end gap-3 border-border/60 border-t px-2 py-1.5">
            <button
              type="button"
              title="Mention a session"
              onClick={() =>
                setDraft({
                  ...draft,
                  text:
                    draft.text && !draft.text.endsWith(' ')
                      ? `${draft.text} @`
                      : `${draft.text}@`,
                })
              }
              className="text-[15px] text-muted-foreground hover:text-foreground"
            >
              @
            </button>
            <button
              type="button"
              onClick={() => void send()}
              disabled={sending || !draft.text.trim()}
              className="rounded-md bg-foreground px-3 py-1 font-semibold text-[12px] text-background disabled:opacity-40"
            >
              Send
            </button>
          </div>
        </div>
      )}
      <div className="mt-1.5 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {readerFor && !resolved && (
            <ReaderLine reader={readerFor(draft.mentions, thread.root.id)} />
          )}
        </div>
        {/* Thread-level whichever comment in it is showing: "dealt with" is a
            property of the question, not of each sentence answering it. */}
        <button
          type="button"
          onClick={() => void onResolve(thread.root.id, !resolved)}
          className="mt-1 shrink-0 text-[11px] text-muted-foreground hover:text-foreground"
        >
          {resolved ? 'Reopen' : 'Resolve'}
        </button>
      </div>
      {error && (
        <p className="mt-1 text-[10px] text-destructive/80" role="alert">
          {error}
        </p>
      )}
    </li>
  )
}

/**
 * Under the last comment while an agent has the thread (RFC 0324 3d, 3g):
 * reading, then working, then its reply, or "read" when it chose not to answer.
 */
export function WaitingLine({ waiting }: { waiting: ThreadWaiting | null }) {
  if (!waiting) return null
  if (waiting.state === 'read') {
    return (
      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span aria-hidden>✓</span>
        Read by {waiting.label}
      </p>
    )
  }
  return (
    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-blue-500" />
      {waiting.label} is {waiting.state}
    </p>
  )
}

/** The line under a comment box saying who will read it (RFC 0324 3c). */
export function ReaderLine({ reader }: { reader: CommentReader }) {
  const warn = reader.tone === 'warn'
  return (
    <p
      className={`mt-1 flex items-center gap-1.5 text-[11px] ${
        warn ? 'text-yellow-500' : 'text-muted-foreground'
      }`}
    >
      <span
        className={`size-1.5 shrink-0 rounded-full ${
          warn ? 'bg-yellow-500' : 'bg-blue-500'
        }`}
      />
      {reader.text}
    </p>
  )
}

export function CommentBody({
  comment,
  now,
  author,
}: {
  comment: CommentEntry
  now: number
  author?: CommentAuthor
}) {
  // One element draws a comment everywhere (RFC 0324 S2): the face, the
  // name, the time, and the body with its mentions as pills. A session's
  // face is its mark (RFC 0311).
  return (
    <CommentRow
      comment={comment}
      author={sessionFace(author ?? unknownProject(comment))}
      now={now}
    />
  )
}

/** A mount that passed no project: name the writer from the comment alone,
 *  which is the stored string for a person and the session for an agent. */
function unknownProject(comment: CommentEntry): CommentAuthor {
  return projectAuthor(undefined, comment)
}
